/**
 * Navigation Component JavaScript
 * 
 * Handles:
 * - Hamburger menu toggle
 * - Menu height calculation for offset adjustments
 * - Fixed menu positioning
 * - Animation support
 * - Accessibility
 */

class Navigation {
	constructor(element, options = {}) {
		this.element = element;
		this.options = {
			animation: options.animation || 'slide-right',
			fixed: options.fixed || false,
			fixedBreakpoint: options.fixedBreakpoint || null,
			autoHeight: options.autoHeight !== false, // Default true
			calculateOffset: options.calculateOffset !== false, // Default true
			...options
		};

		this.toggleButton = document.querySelector('[data-nav-toggle]');
		this.mobileMenu = document.querySelector('.mobile-menu');
		this.overlay = document.querySelector('[data-nav-overlay]');
		this.adminBar = document.querySelector('#wpadminbar');
		this.siteLogo = document.querySelector('header .wp-block-site-logo');
		this.isOpen = false;
		this.scrollPosition = 0; // Store scroll position when menu opens
		this.submenusInitialized = false; // Track if submenus have been initialized

		// Scroll behavior properties
		this.lastScrollY = window.scrollY;
		this.scrollThreshold = 5; // Minimum scroll distance to trigger hide/show
		this.isScrolling = false;
		this.headerHeight = 0; // Will be calculated dynamically
		this.hideOffset = 0; // Scroll position where header starts hiding

		this.init();
	}

	init() {
		// Set animation type
		if (this.options.animation) {
			this.element.setAttribute('data-animation', this.options.animation);
		}

		// Set fixed positioning
		if (this.options.fixed) {
			this.element.setAttribute('data-fixed', 'true');
		} else if (this.options.fixedBreakpoint) {
			this.element.setAttribute('data-fixed-breakpoint', this.options.fixedBreakpoint);
		}

		// Calculate and set menu height
		if (this.options.autoHeight) {
			this.calculateHeight();
			window.addEventListener('resize', () => this.calculateHeight());
		}

		// Calculate offset for first section
		if (this.options.calculateOffset) {
			this.calculateOffset();
			window.addEventListener('resize', () => this.calculateOffset());
		}

		// Initialize toggle button		
		if (this.toggleButton) {
			this.toggleButton.addEventListener('click', (e) => this.handleToggle(e));
		}

		// Close menu on overlay click
		if (this.overlay) {
			this.overlay.addEventListener('click', () => this.close());
		}

		// Close menu on Escape key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.isOpen) {
				this.close();
			}
		});

		// Close menu when clicking outside
		document.addEventListener('click', (e) => {
			if (this.isOpen && !this.element.contains(e.target)) {
				this.close();
			}
		});

		// Handle submenu toggles on mobile
		this.initSubmenus();

		// Initialize scroll behavior for hide/show on scroll
		this.initScrollBehavior();

		// Prevent body scroll when menu is open
		// this.handleBodyScroll();

		this.hoverQuery = window.matchMedia('(hover: none)');
		this.updateNavBehavior(this.hoverQuery);
		this.hoverQuery.addEventListener('change', this.updateNavBehavior);
	}

	/**
	 * Calculate and set navigation height
	 */
	calculateHeight() {
		let height = this.element.offsetHeight;
		let isMobile = window.matchMedia('(max-width: 767px)').matches;

		if (this.adminBar) {
			document.documentElement.style.setProperty('--admin-bar-height', `${this.adminBar.offsetHeight}px`);
		} else {
			document.documentElement.style.setProperty('--admin-bar-height', `0px`);
		}

		document.documentElement.style.setProperty('--nav-height-mobile', `${height}px`);
		document.documentElement.style.setProperty('--nav-height', `${height}px`);
	}

	/**
	 * Calculate offset for first section when menu is fixed
	 */
	calculateOffset() {
		if (!this.options.fixed && !this.options.fixedBreakpoint) {
			return;
		}

		// Check if menu should be fixed at current breakpoint
		const shouldBeFixed = this.shouldBeFixed();

		if (shouldBeFixed) {
			const height = this.element.offsetHeight;
			const firstSection = document.querySelector('main > *:first-child, .entry-content > *:first-child');

			if (firstSection) {
				firstSection.style.scrollMarginTop = `${height}px`;
				firstSection.style.marginTop = '0';
			}
		}
	}

	/**
	 * Check if menu should be fixed at current breakpoint
	 */
	shouldBeFixed() {
		if (this.options.fixed) {
			return true;
		}

		if (this.options.fixedBreakpoint) {
			const breakpoints = {
				tablet: 768,
				desktop: 1024
			};

			const breakpoint = breakpoints[this.options.fixedBreakpoint];
			return window.innerWidth >= breakpoint;
		}

		return false;
	}

	/**
	 * Handle menu toggle
	 */
	handleToggle(e) {
		e.preventDefault();
		this.toggle();
	}

	/**
	 * Toggle menu open/closed
	 */
	toggle() {
		if (this.isOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	/**
	 * Open menu
	 */
	open() {
		this.isOpen = true;

		// Store current scroll position
		this.scrollPosition = window.scrollY;

		if (this.mobileMenu) {
			this.mobileMenu.classList.add('is-open');
			this.mobileMenu.setAttribute('aria-expanded', 'true');
		}

		if (this.toggleButton) {
			this.toggleButton.setAttribute('aria-expanded', 'true');
		}

		if (this.overlay) {
			this.overlay.classList.add('is-visible');
			this.overlay.setAttribute('aria-expanded', 'true');
		}

		// Prevent body scrolling and maintain scroll position
		document.body.classList.add('nav-menu-open');
		document.body.style.top = `-${this.scrollPosition}px`;
	}

	/**
	 * Close menu
	 */
	close() {
		this.isOpen = false;

		if (this.mobileMenu) {
			this.mobileMenu.classList.remove('is-open');
			this.mobileMenu.setAttribute('aria-expanded', 'false');
		}

		if (this.toggleButton) {
			this.toggleButton.setAttribute('aria-expanded', 'false');
		}

		if (this.overlay) {
			this.overlay.classList.remove('is-visible');
			this.overlay.setAttribute('aria-expanded', 'false');
		}

		// Restore body scrolling and scroll position
		document.body.classList.remove('nav-menu-open');
		document.body.style.top = '';
		window.scrollTo(0, this.scrollPosition);

		// Close all submenus
		// this.closeAllSubmenus();

		// Trigger custom event
		this.element.dispatchEvent(new CustomEvent('navigation:close', {
			bubbles: true,
			detail: { navigation: this }
		}));
	}

	/**
	 * Initialize submenu toggles for mobile
	 */
	initSubmenus() {
		// Only initialize once to avoid multiple event listeners
		if (this.submenusInitialized) {
			return;
		}

		// Handle custom submenu toggles (non-WordPress blocks)
		const submenuToggles = this.element.querySelectorAll('[data-submenu-toggle]');

		submenuToggles.forEach(toggle => {
			toggle.addEventListener('click', (e) => {
				e.preventDefault();
				const submenu = toggle.nextElementSibling;

				if (submenu && submenu.classList.contains('main-navigation__submenu')) {
					const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
					toggle.setAttribute('aria-expanded', !isExpanded);
					submenu.setAttribute('aria-expanded', !isExpanded);
				}
			});
		});

		// Brixx classic menu uses core-like markup but not core/navigation Interactivity — toggle aria-expanded here.
		const classicSubmenuToggles = this.element.querySelectorAll(
			'.block-classic-menu .wp-block-navigation-submenu__toggle'
		);
		classicSubmenuToggles.forEach((toggle) => {
			toggle.addEventListener('click', (e) => {
				e.preventDefault();
				const submenu = toggle.nextElementSibling;
				if (!submenu || !submenu.classList.contains('wp-block-navigation__submenu-container')) {
					return;
				}

				const item = toggle.closest('.wp-block-navigation-item.has-child');
				const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

				if (item && item.parentElement) {
					item.parentElement
						.querySelectorAll(':scope > li.has-child > .wp-block-navigation-submenu__toggle')
						.forEach((siblingToggle) => {
							if (siblingToggle !== toggle) {
								siblingToggle.setAttribute('aria-expanded', 'false');
							}
						});
				}

				toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
			});
		});

		this.submenusInitialized = true;
	}

	/**
	 * Close all submenus
	 */
	closeAllSubmenus() {
		// Close custom submenus
		const submenus = this.element.querySelectorAll('.main-navigation__submenu[aria-expanded="true"]');
		submenus.forEach(submenu => {
			submenu.setAttribute('aria-expanded', 'false');
			const toggle = submenu.previousElementSibling;
			if (toggle && toggle.hasAttribute('data-submenu-toggle')) {
				toggle.setAttribute('aria-expanded', 'false');
			}
		});

		// Close WordPress navigation block submenus by clicking their buttons
		// This properly triggers WordPress's Interactivity API
		const wpSubmenuButtons = this.element.querySelectorAll('.wp-block-navigation-item button.wp-block-navigation__submenu-icon[aria-expanded="true"]');
		wpSubmenuButtons.forEach(button => {
			// Trigger a click to let WordPress handle the closing
			button.click();
		});
	}

	/**
	 * Update navigation behavior based on hover state
	 */
	
	updateNavBehavior(e) {
		document.querySelectorAll('.wp-block-navigation-item.has-child').forEach(item => {
			if (e.matches) {
					item.classList.remove('open-on-hover-click');
					item.classList.add('open-on-click');
			} else {
					item.classList.remove('open-on-click');
					item.classList.add('open-on-hover-click');
			}
		});
	}

	/**
	 * Calculate header height for hide offset
	 */
	calculateHeaderHeight() {
		if (!this.siteHeader) {
			return 0;
		}

		// Check if body has fullscreen hero or transparent nav classes
		const hasFullscreenHero = document.body.classList.contains('has-fullscreen-hero');
		const hasTransparentNav = document.body.classList.contains('has-transparent-nav');

		// If fullscreen hero or transparent nav, set header height to 0
		if (hasFullscreenHero || hasTransparentNav) {
			this.headerHeight = 0;
			this.hideOffset = 0;
			document.documentElement.style.setProperty('--header-height', '0px');
			return 0;
		}

		// Force a reflow to ensure all styles are applied
		void this.siteHeader.offsetHeight;

		const isMobile = window.matchMedia('(max-width: 1023px)').matches;
		const navBar = this.siteHeader.querySelector('.site-header__navigation-bar');
		const topBar = this.siteHeader.querySelector('.site-header__top-bar');

		if (topBar) {
			const topBarStyles = window.getComputedStyle(topBar);
		}

		if (isMobile) {
			// On mobile: use only navigation bar height (logo + hamburger)
			// Mobile menu is separate and not part of the fixed header
			this.headerHeight = navBar ? navBar.getBoundingClientRect().height : this.siteHeader.getBoundingClientRect().height;
		} else {
			// On desktop: use full header height (top bar + navigation bar)
			this.headerHeight = this.siteHeader.getBoundingClientRect().height;
		}

		//console.groupEnd();

		// The hide offset is the header height
		this.hideOffset = this.headerHeight;

		// Set CSS variable for body padding
		document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);

		return this.headerHeight;
	}

	/**
	 * Initialize scroll behavior - hide on scroll down, show on scroll up
	 */
	initScrollBehavior() {
		// Find the header element (FSE template part)
		this.siteHeader = document.querySelector('header.wp-block-template-part');

		if (!this.siteHeader) {
			console.warn('Header element not found for scroll behavior');
			return;
		}

		// Calculate initial header height after a small delay to ensure CSS is applied
		// Use requestAnimationFrame to ensure layout is complete
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.calculateHeaderHeight();
			});
		});

		// Set initial state - header is visible by default
		this.siteHeader.classList.add('header-visible');
		this.updateHeaderState();

		// Recalculate header height on window resize
		let resizeTimeout;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				this.calculateHeaderHeight();
			}, 100);
		});

		// Add scroll event listener with debouncing
		let ticking = false;

		window.addEventListener('scroll', () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					this.handleScroll();
					ticking = false;
				});
				ticking = true;
			}
		});
	}

	/**
	 * Handle scroll event
	 */
	handleScroll() {
		const currentScrollY = window.scrollY;
		const scrollDifference = currentScrollY - this.lastScrollY;

		// Update scrolled state (for transparent nav)
		this.updateHeaderState();

		// Only act if scrolled past threshold
		if (Math.abs(scrollDifference) < this.scrollThreshold) {
			return;
		}

		// At the very top - always show header
		if (currentScrollY <= 0) {
			this.siteHeader.classList.remove('header-hidden');
			this.siteHeader.classList.add('header-visible');
		}
		// Scrolling down - hide header (only after scrolling past the header height)
		else if (scrollDifference > 0 && currentScrollY > this.hideOffset) {
			this.siteHeader.classList.add('header-hidden');
			this.siteHeader.classList.remove('header-visible');
		}
		// Scrolling up - show header (anywhere on the page)
		else if (scrollDifference < 0) {
			this.siteHeader.classList.remove('header-hidden');
			this.siteHeader.classList.add('header-visible');
		}

		this.lastScrollY = currentScrollY;
	}

	/**
	 * Update header state based on scroll position
	 */
	updateHeaderState() {
		const currentScrollY = window.scrollY;

		// Add scrolled class if scrolled past 10px
		if (currentScrollY > 10) {
			this.siteHeader.classList.add('header-scrolled');
			this.siteLogo.classList.remove('site-logo-alternative');
		} else {
			this.siteHeader.classList.remove('header-scrolled');
			this.siteLogo.classList.add('site-logo-alternative');
		}
	}

	/**
	 * Handle body scroll prevention when menu is open
	 */
	handleBodyScroll() {
		// Add CSS for body scroll lock
		if (!document.getElementById('nav-scroll-lock-style')) {
			const style = document.createElement('style');
			style.id = 'nav-scroll-lock-style';
			style.textContent = `
				body.nav-menu-open {
					overflow: hidden;
					position: fixed;
					width: 100%;
				}
			`;
			document.head.appendChild(style);
		}
	}

	/**
	 * Update options
	 */
	updateOptions(newOptions) {
		this.options = { ...this.options, ...newOptions };

		// Reapply settings
		if (newOptions.animation) {
			this.element.setAttribute('data-animation', newOptions.animation);
		}

		if (newOptions.fixed !== undefined) {
			if (newOptions.fixed) {
				this.element.setAttribute('data-fixed', 'true');
				this.element.removeAttribute('data-fixed-breakpoint');
			} else {
				this.element.removeAttribute('data-fixed');
			}
		}

		if (newOptions.fixedBreakpoint) {
			this.element.setAttribute('data-fixed-breakpoint', newOptions.fixedBreakpoint);
			this.element.removeAttribute('data-fixed');
		}

		// Recalculate if needed
		if (newOptions.autoHeight || newOptions.calculateOffset) {
			this.calculateHeight();
			this.calculateOffset();
		}
	}
}


/**
 * Check for fullscreen hero and transparent nav
 */
function checkHeroBlockSettings() {
	// Find the first block in the main content
	const firstBlock = document.querySelector('.wp-site-blocks main .entry-content > *:first-child');

	if (!firstBlock) {
		return;
	}

	// Check if it's a fullscreen hero block
	const isFullscreenHero = firstBlock.classList.contains('block-hero') &&
		firstBlock.classList.contains('block-hero--size-fullscreen');

	if (isFullscreenHero) {
		document.body.classList.add('has-fullscreen-hero');

		// Check if it has transparent navigation enabled
		const hasTransparentNav = firstBlock.hasAttribute('data-transparent-nav');

		if (hasTransparentNav) {
			document.body.classList.add('has-transparent-nav');
		}
	}
}

function initNavigation() {
	// Check for hero block settings first
	checkHeroBlockSettings();

	document.querySelector('body header.wp-block-template-part').setAttribute('data-navigation', 'true');
	// document.querySelector('body header.wp-block-template-part').setAttribute('data-fixed', 'true');

	const element = document.querySelector('[data-navigation]');

	const options = {
		animation: element.getAttribute('data-animation') || 'slide-right',
		fixed: element.getAttribute('data-fixed') === 'true',
		fixedBreakpoint: element.getAttribute('data-fixed-breakpoint'),
		autoHeight: element.getAttribute('data-auto-height') !== 'false',
		calculateOffset: element.getAttribute('data-calculate-offset') !== 'false'
	};

	// Initialize navigation
	const navigation = new Navigation(element, options);

	// Store instance on element for external access
	element.navigationInstance = navigation;
}
/**
 * Initialize all navigation instances
 */
/*
function initNavigations() {
	// 
	document.querySelector('body header.wp-block-template-part').setAttribute('data-navigation', 'true');


	const navigationElements = document.querySelectorAll('[data-navigation]');
	
	navigationElements.forEach(element => {
		// Get options from data attributes
		const options = {
			animation: element.getAttribute('data-animation') || 'slide-right',
			fixed: element.getAttribute('data-fixed') === 'true',
			fixedBreakpoint: element.getAttribute('data-fixed-breakpoint'),
			autoHeight: element.getAttribute('data-auto-height') !== 'false',
			calculateOffset: element.getAttribute('data-calculate-offset') !== 'false'
		};
		
		// Initialize navigation
		const navigation = new Navigation(element, options);
		
		// Store instance on element for external access
		element.navigationInstance = navigation;
	});
}
*/

/**
 * Initialize FSE navigation blocks
 * /
 /*
function initFSENavigation() {
	const fseNavs = document.querySelectorAll('.wp-block-navigation');
	
	fseNavs.forEach(nav => {
		const toggle = nav.querySelector('.wp-block-navigation__responsive-container-open');
		const closeButton = nav.querySelector('.wp-block-navigation__responsive-container-close');
		
		if (toggle) {
			// Get animation from data attribute or default
			const animation = nav.getAttribute('data-animation') || 'slide-right';
			nav.setAttribute('data-animation', animation);
		}
		
		// Calculate height for FSE navs too
		const height = nav.offsetHeight;
		document.documentElement.style.setProperty('--nav-height', `${height}px`);
	});
}
*/

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		// initNavigations();
		// initFSENavigation();
		initNavigation();
	});
} else {
	// initNavigations();
	//initFSENavigation();
	initNavigation();
}

// Recalculate header height after all resources (fonts, images) are loaded
window.addEventListener('load', () => {
	const header = document.querySelector('header.wp-block-template-part');
	if (header && header.navigationInstance) {
		header.navigationInstance.calculateHeaderHeight();
	}
});

// Export for use in other modules
export default Navigation;
// export { initNavigations, initFSENavigation };
export { initNavigation, checkHeroBlockSettings };